import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { completeNewPasswordChallenge } from '../services/auth.service';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [challengeData, setChallengeData] = useState(null);

  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the page user was trying to access (from ProtectedRoute)
  const from = location.state?.from?.pathname || '/account';

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/account', { replace: true });
    }
  }, [user, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      // Check if new password is required
      if (result?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeData(result);
        setLoading(false);
        return;
      }

      // Redirect to intended page or account page
      navigate(from, { replace: true });
    } catch (err) {
      // Handle Cognito error messages
      let errorMessage = 'Failed to sign in';

      if (err.code === 'UserNotFoundException') {
        errorMessage = 'User not found. Please check your email.';
      } else if (err.code === 'NotAuthorizedException') {
        errorMessage = 'Incorrect email or password.';
      } else if (err.code === 'UserNotConfirmedException') {
        errorMessage = 'Please verify your email before logging in.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPasswordSubmit(e) {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength (basic check)
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      await completeNewPasswordChallenge(
        challengeData.cognitoUser,
        newPassword,
        challengeData.userAttributes
      );

      // Need to login again after password change
      await login(email, newPassword);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  }

  // Show new password form if challenge is required
  if (challengeData) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="bg-logo-pink text-white py-5">
          <div className="max-w-7xl mx-auto px-4">
            <Link to="/" className="font-logo text-3xl tracking-wide hover:opacity-90 transition">
              Make Merry
            </Link>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full space-y-6">
            {/* Header */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Set New Password</h2>
              <p className="mt-2 text-gray-600">
                Your password needs to be changed. Please enter a new password.
              </p>
            </div>
            {/* Error Message */}
            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {/* New Password Form */}
            <form className="space-y-6" onSubmit={handleNewPasswordSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    id="new-password"
                    name="new-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                    placeholder="New password (min 8 characters)"
                    aria-label="New password"
                  />
                </div>
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                    placeholder="Confirm new password"
                    aria-label="Confirm password"
                  />
                </div>
              </div>

              <div className="text-sm bg-pink-50 p-4 rounded-lg border border-pink-200">
                <p className="font-medium text-gray-900 mb-2">Password requirements:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains numbers</li>
                  <li>Contains special characters</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-logo-pink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-logo-pink disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? 'Setting password...' : 'Set new password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="bg-logo-pink text-white py-5">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/" className="font-logo text-3xl tracking-wide hover:opacity-90 transition">
            Make Merry
          </Link>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-6">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
            <p className="mt-2 text-gray-600">
              Welcome back! Sign in to your account.
            </p>
          </div>
          {/* Error Message */}
          {error && (
            <div className="rounded-lg bg-red-50 p-4 border border-red-200">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form className="space-y-6" onSubmit={handleSubmit} autoComplete="on">
            <div className="space-y-4">
              <div>
                <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="username email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                  placeholder="you@example.com"
                  aria-label="Email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-logo-pink focus:border-transparent"
                  placeholder="Enter your password"
                  aria-label="Password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-logo-pink hover:opacity-80 transition"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-logo-pink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-logo-pink disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="font-medium text-logo-pink hover:opacity-80 transition"
              >
                Sign up
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="text-center pt-4">
            <Link
              to="/"
              className="text-sm text-gray-500 hover:text-gray-700 transition"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
